import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService, CommonService, UrlService } from '@app/shared/services';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { LoginStoreService } from './root-store/login-store/login-store.service';
import { environment } from '@env/environment';
import { UserTypeEnum } from './enum/UserTypeEnum';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MxTaskbarService } from './shared/services/mx-taskbar/mx-taskbar.service';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { HomeSubjectService } from './modules/home-page/container/home-subject.service';
import { NetworkSetupNavService } from './shared/services/network-setup-nav/network-setup-nav.service';
import { Hub } from '@aws-amplify/core';
import { Store } from '@ngrx/store';
import { RootStoreState, loginStoreActions, relationshipStoreActions, vaultStoreActions } from './root-store';
import { GraphQLService } from './shared/services/graphql/graphql.service';
import { RealtimeEventService } from './shared/services/realtime-event/realtime-event.service';
import { KAI_EVENT_TYPES } from './modules/kinnect-ai/kinnect-ai.types';
import { CommonModule } from '@angular/common';
import { DiscoveryConfigurationComponent } from './modules/on-boarding/discovery-configuration/discovery-configuration.component';
import { AccountSetupComponent } from './shared/component/account-setup/account-setup.component';
import { CommonUploadDialogComponent } from './shared/component/common-upload-dialog/common-upload-dialog.component';
import { ManageMxTaskbarComponent } from './shared/component/manage-mx-taskbar/manage-mx-taskbar.component';
import { NetworkResponsibilitiesComponent } from './shared/component/network-setup/network-responsibilities/network-responsibilities.component';
import { NetworkSetupComponent } from './shared/component/network-setup/network-setup.component';
import { SecurityPrivacySetupComponent } from './shared/component/security-privacy-setup/security-privacy-setup.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdleService } from './shared/services/idle/idle.service';
import { SharedLogoutComponent } from './shared/component/shared-logout/shared-logout.component';
import {
  AiTokenLimitRealtimeEventData,
  buildAiTokenLimitDialogViewModel,
} from './shared/component/ai-token-limit-dialog/ai-token-limit-dialog.util';
import { HomeObsService } from './modules/home-page/home/home-obs.service';
import { QuickListDrawerService } from './modules/project/components/project-view/cells/link-cell/quick-list-drawer/quick-list-drawer.service';
import { LinkCellSuggestionDrawerService } from './modules/project/components/project-view/cells/link-cell/link-cell-suggestion-drawer/link-cell-suggestion-drawer.service';
@Component({
  selector: 'app-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  imports: [
    CommonModule,
    RouterOutlet,
    MatSidenavModule,
    CommonUploadDialogComponent,
    ManageMxTaskbarComponent,
    AccountSetupComponent,
    SecurityPrivacySetupComponent,
    NetworkSetupComponent,
    NetworkResponsibilitiesComponent,
    DiscoveryConfigurationComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('homePageDrawer') public sidenav: MatSidenav;
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private urlService = inject(UrlService);
  private dialog = inject(MatDialog);
  private ccService = inject(NgcCookieConsentService);
  private homeObsService = inject(HomeObsService);
  private loginService = inject(LoginStoreService);
  private mxTaskbarService = inject(MxTaskbarService);
  private toastr = inject(ToastrService);
  private modalService = inject(NgbModal);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private cd = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private homeSubjectService = inject(HomeSubjectService);
  private networkSetupNavService = inject(NetworkSetupNavService);
  private store$ = inject(Store<RootStoreState.State>);
  private graphqlService = inject(GraphQLService);
  private realtimeEventService = inject(RealtimeEventService);
  private readonly idleService = inject(IdleService);
  private readonly quickListDrawer = inject(QuickListDrawerService);
  private readonly suggestionDrawer = inject(LinkCellSuggestionDrawerService);

  private hubListenerCleanup: (() => void) | undefined;
  private realtimeSubscription: Subscription | null = null;
  private readonly realtimeRetryDelayMs = 3000;
  private readonly realtimeMaxRetries = 5;
  private realtimeRetryCount = 0;
  /** Cleared on logout so realtime can subscribe again after re-login. */
  private cognitoUserId?: string;
  private logoutDialogRef: any;
  private aiTokenLimitDialogRef: MatDialogRef<unknown> | null = null;
  recaptchaSiteKey: string;
  previousUrl: string = null;
  currentUrl: string = null;
  isUserLoggedIn = false;
  openSideBar = false;
  accountSetupOpened = false;
  introToKinnectOpened = false;
  onboardingResponsibilityOpened = false;
  openDiscoveryCategoryConfig = false;
  networkSetupOpened = false;
  securityPrivacySetupOpened = false;
  accountType: string = 'showInstruction';
  discoveryCategoryConfigType: any;
  switchFromUser = false;
  subscription = new Subscription();

  constructor() {
    this.recaptchaSiteKey = environment.recaptcha_siteKey;
  }

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: NavigationEnd) => {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        this.urlService.setPreviousUrl(this.previousUrl);
      });

    this.loginService.loginObject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (state) => {
      const loginStateObject = state.data;
      let authUser: Awaited<ReturnType<typeof this.authService.getUserAttributes>> | undefined;
      try {
        authUser = await this.authService.getUserAttributes();
      } catch {
        this.teardownRealtimeConnection();
        this.idleService.stop();
        this.cd.markForCheck();
        return;
      }

      if (loginStateObject?.switch_from_user_id && loginStateObject?.id !== authUser['custom:userid']) {
        this.switchFromUser = loginStateObject?.switchFromUser;
      } else {
        this.switchFromUser = null;
      }

      const nextUserId = authUser?.['custom:userid'];
      if (nextUserId) {
        // Reconnect when user id changes or after logout cleared `cognitoUserId` (same user logging in again).
        if (this.cognitoUserId !== nextUserId) {
          this.cognitoUserId = nextUserId;
          this.cd.markForCheck();
          this.realtimeRetryCount = 0;
          this.connectRealtimePublishEvent(nextUserId);
        }
        this.idleService.start();
        this.cd.markForCheck();
      }
    });

    this.subscription.add(
      this.authService.$user.subscribe((data) => {
        if (!data?.id) {
          this.teardownRealtimeConnection();
          this.idleService.stop();
          this.cd.markForCheck();
          return;
        }
        const nextUserId = String(data.id);
        if (this.cognitoUserId !== nextUserId) {
          this.cognitoUserId = nextUserId;
          this.cd.markForCheck();
          this.realtimeRetryCount = 0;
          this.connectRealtimePublishEvent(nextUserId);
        }
        this.idleService.start();
        this.cd.markForCheck();
      })
    );

    this.checkIfUserAuthenticated();

    this.hubListenerCleanup = Hub.listen('auth', (data) => {
      const { event } = data.payload;
      if (event === 'signedIn') {
        this.isUserLoggedIn = true;
      } else if (event === 'signedOut') {
        this.isUserLoggedIn = false;
        this.teardownRealtimeConnection();
        this.quickListDrawer.close();
        this.suggestionDrawer.close();
      }
    });

    this.ccService.statusChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: any) => {
      if (event.status === 'dismiss') {
        this.authService.setCookie('cookieconsent_status', 'dismiss', 365);
      } else if (event.status === 'deny') {
        this.authService.clearCookie('userConsent');
      }
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const state = window.history.state;
      const nextStep = state?.setupStep;

      const stepSetupHandlers = {
        intro_to_kinnect_setup_status: () => (this.introToKinnectOpened = true),
        account_setup_status: () => (this.accountSetupOpened = true),
        security_and_privacy_setup_status: () => (this.securityPrivacySetupOpened = true),
        network_setup_status: () => (this.networkSetupOpened = true),
        onboarding_responsibility_complete_status: () => (this.onboardingResponsibilityOpened = true)
      };

      if (nextStep && stepSetupHandlers[nextStep]) {
        setTimeout(() => {
          stepSetupHandlers[nextStep]();
          this.cd.markForCheck();
        }, 10000);
      }
    });

    this.homeSubjectService.fetchSetupData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data) {
        this.openSetupItemCardEvent(data);
      }
    });

    this.homeSubjectService.closeSetupItemCard.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data) {
        this.closeIntroSetupSideBarEventEmitter();
      }
    });

    this.homeSubjectService.fetchDiscoverySetupData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.openDiscoverySetupItemCardEvent(data);
    });

    this.networkSetupNavService.isOpened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (!data) this.closeDrawerEventHandler();
    });

    this.subscribeToIdleEvents();
  }

  openBalanceSheetSideBar() {
    this.openSideBar =
      this.introToKinnectOpened ||
      this.accountSetupOpened ||
      this.securityPrivacySetupOpened ||
      this.networkSetupOpened ||
      this.onboardingResponsibilityOpened ||
      this.openDiscoveryCategoryConfig;
  }

  async checkIfUserAuthenticated(): Promise<void> {
    const authUser = await this.authService.getUserAttributes();
    if (!authUser) {
      this.isUserLoggedIn = false;
      this.idleService.stop();
      return;
    }
    this.isUserLoggedIn = true;
    const userId = authUser['custom:userid'];
    this.store$.dispatch(new loginStoreActions.LoginRequestAction());
    this.store$.dispatch(new relationshipStoreActions.AllRelationships());
    if (userId) {
      this.store$.dispatch(new vaultStoreActions.GetVaultRequestAction(userId, false));
    }
    this.idleService.start();
  }

  private disconnectRealtimePublishEvent(): void {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
      this.realtimeSubscription = null;
    }
  }

  /** Tear down AppSync subscription and user id so the next login opens a fresh websocket. */
  private teardownRealtimeConnection(): void {
    this.disconnectRealtimePublishEvent();
    this.cognitoUserId = undefined;
    this.realtimeRetryCount = 0;
  }

  private connectRealtimePublishEvent(userId: string): void {
    this.disconnectRealtimePublishEvent();

    this.realtimeSubscription = this.graphqlService.onPublishEvent(userId).subscribe({
      next: (res: any) => {
        this.realtimeRetryCount = 0;
        if (res?.data?.type === 'DOCUMENT_TEMPLATE') {
          this.realtimeEventService.triggerDocumentRefresh(res);
        } else if (res?.data?.type === 'MIGRATE_TEMPLATES') {
          try {
            JSON.parse(res.data.meta_data)?.status === 'end'
              ? this.realtimeEventService.triggerDocumentRefresh(res)
              : null;
          } catch (error) {}
        } else if (res?.data?.type === 'REPORT_EXECUTION_END') {
          this.realtimeEventService.triggerReportViewRefresh(res);
        } else if (res?.data?.type === 'CHECKLIST_TASK_UPDATED') {
          this.realtimeEventService.triggerDocumentRefresh(res);
        } else if (res?.data?.type === 'QUILTT_ACCOUNTS_SYNCED_INITIAL') {
          this.realtimeEventService.triggerDocumentRefresh(res);
        } else if (
          res?.data?.type === 'AI_TOKEN_LIMIT' ||
          res?.data?.type === 'AI_TOKEN_LIMIT_EXCEEDED'
        ) {
          void this.openAITokenLimitDialog(res?.data);
        } else if (KAI_EVENT_TYPES.includes(res?.data?.type)) {
          this.realtimeEventService.triggerDocumentRefresh(res);
        }
      },
      error: (err) => {
        if (this.realtimeRetryCount < this.realtimeMaxRetries && this.cognitoUserId) {
          this.realtimeRetryCount += 1;
          setTimeout(() => this.connectRealtimePublishEvent(this.cognitoUserId), this.realtimeRetryDelayMs);
        }
      }
    });
  }

  async openAITokenLimitDialog(data: AiTokenLimitRealtimeEventData): Promise<void> {
    const { AiTokenLimitDialogComponent } = await import(
      './shared/component/ai-token-limit-dialog/ai-token-limit-dialog.component'
    );
    this.aiTokenLimitDialogRef?.close();
    this.aiTokenLimitDialogRef = this.dialog.open(AiTokenLimitDialogComponent, {
      width: '427px',
      maxWidth: '95vw',
      autoFocus: false,
      data: buildAiTokenLimitDialogViewModel(data),
      panelClass: 'ai-token-limit-dialog-panel',
    });
    this.aiTokenLimitDialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.aiTokenLimitDialogRef = null;
      });
  }

  onLogout() {
    this.idleService.stop();

    this.teardownRealtimeConnection();
    const user = this.authService.user;
    const logoutURL = user.user_type === UserTypeEnum.Professional ? '/' : '/';

    this.authService
      .logoutWithGuardianCheck()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.commonService.resetManageQuickAddEntityRegister(false);
          this.dialog.closeAll();
          this.modalService.dismissAll('user logged out');
          this.mxTaskbarService.clearMinimizedDialogs();
          this.cd.markForCheck();
          this.router.navigate([logoutURL]);
          this.homeObsService.handleFetchDataAuditSideNavState({
            open: false
          });
        },
        error: (error: any) => {
          this.toastr.error(error.error.message);
          return false;
        }
      });
  }

  private subscribeToIdleEvents(): void {
    this.idleService.idle$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event.type === 'countdown') {
        this.handleIdleCountdown(event.remaining);
      } else if (event.type === 'timeout') {
        this.handleIdleTimeout();
      }
    });
  }

  private handleIdleCountdown(remaining: number): void {
    if (this.isAuthRoute()) return;

    if (!this.logoutDialogRef) {
      this.logoutDialogRef = this.dialog.open(SharedLogoutComponent, {
        disableClose: true,
        data: { counter: remaining }
      });

      this.logoutDialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((stayLoggedIn: boolean) => {
          this.logoutDialogRef = null;
          if (stayLoggedIn) {
            this.idleService.reset();
          } else {
            this.onLogout();
          }
          this.cd.markForCheck();
        });
    } else {
      this.logoutDialogRef.componentInstance?.updateCounter(remaining);
    }
  }

  private handleIdleTimeout(): void {
    if (this.isAuthRoute()) {
      this.logoutDialogRef?.close(false);
      this.idleService.stop();
      return;
    }

    this.logoutDialogRef?.close(false);
    this.onLogout();
  }

  private isAuthRoute(): boolean {
    const path = this.router.url.split('?')[0].split('#')[0];
    return (
      path === '/' ||
      path === '/oauth-return' ||
      path === '/signup' ||
      path === '/signup-family' ||
      path === '/signup-professional' ||
      path === '/forgot-password' ||
      path === '/forgot-password-professional'
    );
  }

  ngOnDestroy() {
    this.idleService.stop();
    this.disconnectRealtimePublishEvent();
    if (this.logoutDialogRef) {
      this.logoutDialogRef.close();
      this.logoutDialogRef = null;
    }
    this.aiTokenLimitDialogRef?.close();
    this.aiTokenLimitDialogRef = null;
    if (this.hubListenerCleanup) {
      this.hubListenerCleanup();
    }
  }

  closeDrawerEventHandler() {
    this.homeSubjectService.handleFetchSetupData(null);
    this.resetFalseAll();
    this.sidenav?.close();
  }

  closeDiscoveryCategoryConfigEventEmitter() {
    this.openDiscoveryCategoryConfig = false;
    this.openBalanceSheetSideBar();
    this.homeSubjectService.handleFetchDiscoveryClosed(true);
  }

  openSetupItemCardEvent(event) {
    if (!event) {
      return;
    }
    if (event === 'intro_to_kinnect') {
      this.introToKinnectOpened = true;
      this.openBalanceSheetSideBar();
    } else if (event === 'account_setup') {
      this.accountSetupOpened = true;
      this.openBalanceSheetSideBar();
    } else if (event === 'security_and_privacy') {
      this.securityPrivacySetupOpened = true;
      this.openBalanceSheetSideBar();
    } else if (event === 'network_setup') {
      this.networkSetupOpened = true;
      this.openBalanceSheetSideBar();
    } else if (event === 'onboarding_responsibility_complete') {
      this.onboardingResponsibilityOpened = true;
      this.openBalanceSheetSideBar();
    } else if (event === 'closed') {
      this.resetFalseAll();
      this.openBalanceSheetSideBar();
    }
  }

  closeIntroSetupSideBarEventEmitter() {
    this.resetFalseAll();
    this.sidenav.close();
  }

  openDiscoverySetupItemCardEvent(event) {
    if (!event) {
      return;
    }
    this.openDiscoveryCategoryConfig = true;
    this.discoveryCategoryConfigType = event;
    this.openBalanceSheetSideBar();
  }

  resetFalseAll() {
    this.accountSetupOpened = false;
    this.networkSetupOpened = false;
    this.securityPrivacySetupOpened = false;
    this.introToKinnectOpened = false;
    this.onboardingResponsibilityOpened = false;
    this.openDiscoveryCategoryConfig = false;
    this.openBalanceSheetSideBar();
    this.homeSubjectService.handleFetchSetupData(null);
  }

  private loadRecaptchaScript(): void {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.recaptchaSiteKey}`;
    document.head.appendChild(script);
  }

  handleMainSideNav(val: any) {
    this.homeSubjectService.handleFetchMainSideNav(val);
  }
}
